import { Backdrop } from '@material-ui/core'
import { makeStyles } from '@material-ui/styles'
import Actions from 'actions'
import { ROUTES } from 'consts'
import { TreatmentTypes } from 'consts/treatmentConsts'
import useFeatureFlags from 'hooks/useFeatureFlags'
import useQueryParams from 'hooks/useQueryParams'
import moment from 'moment'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router'
import WebappOverlayLoader from 'resources/webapp-overlay-loader.gif'
import { trackEvent } from 'utils/analyticsUtils'
import useRolePermissions from 'hooks/useRolePermissions'
import { readPmsParams } from './pmsQueryParams'

// How long to wait for the profile before giving up rather than leaving the doctor on a loader
// that never resolves.
const PROFILE_WAIT_MS = 15000

const useStyles = makeStyles(theme => ({
  loader: {
    zIndex: 999,
    backgroundColor: '#f5f5f590',
    color: 'var(--border-color-5)'
  }
}))

const Landing = props => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const history = useHistory()
  const { queryParams: rawQueryParams } = useQueryParams()
  // Vendor-controlled and possibly duplicated, which query-string returns as an array.
  const queryParams = useMemo(() => ({ ...rawQueryParams, ...readPmsParams(rawQueryParams) }), [rawQueryParams])

  const { doctor } = useSelector(state => state.profileReducer)
  const { createRecordsOnlyPatients: createRecordsOnlyPatientsFF } = useFeatureFlags()
  const { permissions } = useRolePermissions()
  const hasRequestedCreateOrAdd = useRef(false)
  const [hasTimedOut, setHasTimedOut] = useState(false)

  const { PatientID: pmsPatientId, PatientName: pmsPatientName, pms, action } = queryParams

  // Published lowercase as `createoradd` while the older action is PascalCase, so accept any casing.
  const isCreateOrAdd = useMemo(() => (action || '').toLowerCase() === 'createoradd', [action])

  useEffect(() => {
    // Names, ids and birth dates are deliberately not sent: trackEvent forwards every property to
    // Mixpanel unredacted, and the createoradd link is the one that carries a DOB.
    trackEvent('PMS Redirect', {
      pms,
      action,
      hasPatientId: !!pmsPatientId,
      hasPatientName: !!pmsPatientName,
      hasGrinPatientId: !!queryParams.GrinPatientID,
      hasFirstName: !!queryParams.PatientFirstName,
      hasLastName: !!queryParams.PatientLastName,
      hasBirthDate: !!queryParams.PatientBirthDate
    })
  }, [pms, pmsPatientName, pmsPatientId, action, queryParams])

  useEffect(() => {
    if (isCreateOrAdd) {
      return
    }

    if (action === 'CreateRecordsPatient') {
      history.push(ROUTES.PATIENTS)
      const birthDate = moment(queryParams.PatientBirthDate, 'MM/DD/YYYY')
      dispatch(
        Actions.setCreateRecordsPatientModalOpen({
          isOpen: true,
          initialValues: {
            firstName: queryParams.PatientFirstName,
            lastName: queryParams.PatientLastName,
            birthDate: birthDate.isValid() ? birthDate.toDate() : null,
            note: queryParams.PatientID
          }
        })
      )
    } else if (action === 'InvitePatient') {
      history.push(ROUTES.PATIENTS)

      const currentYear = new Date().getFullYear()
      const birthYear = parseInt(queryParams.PatientBirthYear, 10)
      const isUnder18 = birthYear && currentYear - birthYear < 18

      let treatmentType = ''
      if (queryParams.PatientTxType) {
        if (queryParams.PatientTxType.toLowerCase().includes('aligner')) {
          treatmentType = TreatmentTypes.Aligners
        } else if (queryParams.PatientTxType.toLowerCase().includes('brace')) {
          treatmentType = TreatmentTypes.Braces
        } else {
          treatmentType = TreatmentTypes.Other
        }
      }

      const phone = queryParams.PatientPhone || ''

      dispatch(Actions.setInvitePatientModalVisibility(true))

      dispatch(
        Actions.setInvitePatientPresetDetails({
          firstName: queryParams.PatientFirstName || '',
          lastName: queryParams.PatientLastName || '',
          email: queryParams.PatientEmail || '',
          phone,
          treatmentType,
          isUnder18: isUnder18 || false,
          pms: queryParams.pms || '',
          // The PMS patient this invite is for. Carried so the invite can be linked back to the PMS
          // (pmsLinkInvitedPatientEpic) — the replacement for the appointment automation we dropped.
          pmsPatientId: queryParams.PatientID || '',
          ...(isUnder18
            ? {
                guardianFirstName: queryParams.LGFirstName || '',
                guardianLastName: queryParams.LGLastName || '',
                guardianEmail: queryParams.LGEmail || ''
              }
            : {})
        })
      )

      trackEvent('PMS Redirect - Invite Patient', {
        pms: queryParams.pms,
        treatmentType,
        isUnder18,
        hasEmail: !!queryParams.PatientEmail,
        hasPhone: !!phone
      })
    } else {
      if (!pmsPatientId && !pmsPatientName) {
        history.push(ROUTES.PATIENTS)
        dispatch(Actions.pmsRedirectFailed({ message: 'Missing query params', pmsPatientId, pmsPatientName }))
      } else {
        dispatch(
          Actions.pmsRedirectSearchLinkedPatient({
            pmsPatientId,
            pmsPatientName
          })
        )
      }
    }
  }, [dispatch, history, pmsPatientId, pmsPatientName, queryParams, action, isCreateOrAdd])

  useEffect(() => {
    // The search scopes by doctor and the create path depends on a plan flag, so both need the
    // profile loaded before this can resolve correctly. Fire once it is, and only once.
    //
    // `username` is required, not just `id`: buildPatientSearchFilter scopes on doctorUsername,
    // and an undefined value serializes away, which would leave the query unscoped across
    // practices.
    if (!isCreateOrAdd || !doctor?.id || !doctor?.username || hasRequestedCreateOrAdd.current) {
      return
    }

    hasRequestedCreateOrAdd.current = true

    dispatch(
      Actions.pmsCreateOrAddSearchPatient({
        firstName: queryParams.PatientFirstName,
        lastName: queryParams.PatientLastName,
        birthDate: queryParams.PatientBirthDate,
        patientId: queryParams.PatientID,
        // The Grin patient id the PMS stored when the patient was linked. When it resolves inside
        // this practice the patient opens directly; anything else (empty, stale, another
        // practice's) falls through to the name + birth-date match, so the link still works.
        grinPatientId: queryParams.GrinPatientID,
        source: queryParams.source || pms,
        // Every other create entry point (AppHeaderV2, AppHeader, AdvancedActions) also requires
        // the permission, so a quick link must not offer creation to a role the rest of the UI
        // never offers it to.
        canCreatePatient: !!createRecordsOnlyPatientsFF && !!permissions.invitePatient
      })
    )
  }, [createRecordsOnlyPatientsFF, permissions, dispatch, doctor, isCreateOrAdd, pms, queryParams])

  useEffect(() => {
    if (!isCreateOrAdd || hasRequestedCreateOrAdd.current) {
      return undefined
    }

    // The component renders an unconditional loader and the gate above has no failure branch, so a
    // profile that never arrives with both id and username would strand the doctor here forever.
    const timer = setTimeout(() => setHasTimedOut(true), PROFILE_WAIT_MS)

    return () => clearTimeout(timer)
  }, [isCreateOrAdd, doctor])

  useEffect(() => {
    if (!hasTimedOut || hasRequestedCreateOrAdd.current) {
      return
    }

    history.push(ROUTES.PATIENTS)
    dispatch(
      Actions.pmsRedirectFailed({
        message: 'Timed out waiting for the doctor profile on the createoradd action',
        hasDoctorId: !!doctor?.id,
        hasDoctorUsername: !!doctor?.username
      })
    )
  }, [hasTimedOut, dispatch, history, doctor])

  return (
    <Backdrop id="pms-landing-loader" className={classes.loader} open>
      <img src={WebappOverlayLoader} alt="Loader" />
    </Backdrop>
  )
}

export default Landing
